import { supabase } from "@/integrations/supabase/client"
import type { TablesInsert } from "@/integrations/supabase/types"

interface CriarAvaliacaoParams {
  lojaId: string
  veiculoId: string
  observacoesIniciais?: string
  objetivoComercial?: "compra" | "consignacao"
  limiteAvaliacao?: number
  inicioPeriodo?: Date
}

interface CriarAvaliacaoResultado {
  id: string
  reusedExisting: boolean
}

export async function criarAvaliacaoParaVeiculo({
  lojaId,
  veiculoId,
  observacoesIniciais = "",
  objetivoComercial,
  limiteAvaliacao,
  inicioPeriodo,
}: CriarAvaliacaoParams): Promise<CriarAvaliacaoResultado> {
  const { data: avaliacaoExistente, error: avaliacaoExistenteError } = await supabase
    .from("avaliacoes")
    .select("id, status, created_at")
    .eq("veiculo_id", veiculoId)
    .in("status", ["pendente", "ativa", "em_andamento"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle()

  if (avaliacaoExistenteError) {
    throw avaliacaoExistenteError
  }

  // Avaliação em andamento: reutiliza sem contar no limite
  if (avaliacaoExistente) {
    return { id: avaliacaoExistente.id, reusedExisting: true }
  }

  // Verifica limite (mensal para planos, total para trial)
  if (limiteAvaliacao !== undefined) {
    let query = supabase
      .from("avaliacoes")
      .select("id", { count: "exact", head: true })
      .eq("loja_id", lojaId)

    if (inicioPeriodo) {
      query = query.gte("created_at", inicioPeriodo.toISOString())
    }

    const { count } = await query

    if ((count ?? 0) >= limiteAvaliacao) {
      const err = new Error(
        inicioPeriodo
          ? "Limite de avaliações atingido para este mês."
          : "Você usou suas 5 avaliações gratuitas."
      )
      ;(err as any).code = inicioPeriodo ? "LIMITE_ATINGIDO" : "LIMITE_TRIAL"
      throw err
    }
  }

  const { data, error } = await supabase
    .from("avaliacoes")
    .insert({
      loja_id: lojaId,
      veiculo_id: veiculoId,
      status: "pendente",
      objetivo_comercial: objetivoComercial ?? null,
      observacoes_iniciais: observacoesIniciais,
    } satisfies TablesInsert<"avaliacoes">)
    .select("id")
    .single()

  if (error) {
    throw error
  }

  return { id: data.id, reusedExisting: false }
}
